import { useState, useCallback } from 'react';
import { useParams, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Breadcrumbs,
  Link,
  Skeleton,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import BuildIcon from '@mui/icons-material/Build';
import { trpc } from '../../lib/trpc';
import { StatusPageMonitorRow } from '../../components/status-page';
import type { MonitorStatus as StatusPageMonitorStatus } from '../../components/status-page';
import { IncidentCard } from '../../components/status-page';
import type { IncidentData } from '../../components/status-page';

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Loading skeleton for the detail page
 */
function DetailSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rectangular" width={80} height={80} sx={{ borderRadius: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="30%" height={24} />
          </Box>
        </Box>
      </Paper>
      <Skeleton variant="rounded" height={200} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" height={300} />
    </Box>
  );
}

/**
 * Create incident dialog
 */
function CreateIncidentDialog({
  open,
  onClose,
  monitors,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  monitors: Array<{ id: string; monitorId: string; displayName: string }>;
  onSubmit: (data: { monitorId: string; cause: string; title?: string; message?: string }) => void;
  isLoading: boolean;
}) {
  const [selectedMonitor, setSelectedMonitor] = useState('');
  const [cause, setCause] = useState('MAINTENANCE');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const handleSubmit = () => {
    if (!selectedMonitor || !cause) return;
    const submitData: { monitorId: string; cause: string; title?: string; message?: string } = {
      monitorId: selectedMonitor,
      cause,
    };
    const trimmedTitle = title.trim();
    const trimmedMessage = message.trim();
    if (trimmedTitle) submitData.title = trimmedTitle;
    if (trimmedMessage) submitData.message = trimmedMessage;
    onSubmit(submitData);
  };

  const handleClose = () => {
    setSelectedMonitor('');
    setCause('MAINTENANCE');
    setTitle('');
    setMessage('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Manual Incident</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create a manual incident for maintenance announcements or other scheduled downtime.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Affected Monitor</InputLabel>
            <Select
              value={selectedMonitor}
              onChange={(e) => setSelectedMonitor(e.target.value)}
              label="Affected Monitor"
            >
              {monitors.map((monitor) => (
                <MenuItem key={monitor.monitorId} value={monitor.monitorId}>
                  {monitor.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth required>
            <InputLabel>Incident Type</InputLabel>
            <Select value={cause} onChange={(e) => setCause(e.target.value)} label="Incident Type">
              <MenuItem value="MAINTENANCE">Scheduled Maintenance</MenuItem>
              <MenuItem value="DEGRADED_PERFORMANCE">Degraded Performance</MenuItem>
              <MenuItem value="PARTIAL_OUTAGE">Partial Outage</MenuItem>
              <MenuItem value="MAJOR_OUTAGE">Major Outage</MenuItem>
              <MenuItem value="INVESTIGATING">Investigating Issue</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Scheduled maintenance window"
            helperText="Optional - A brief title for the incident"
            fullWidth
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="We will be performing routine maintenance..."
            helperText="Optional - Additional details about the incident"
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedMonitor || !cause || isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Incident'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Status page detail component
 * Displays and manages a single status page
 */
export function StatusPageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const locationState = location.state as { message?: string } | null;

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(!!locationState?.message);
  const [snackbarMessage, setSnackbarMessage] = useState(locationState?.message || '');

  // Create incident dialog state
  const [incidentDialogOpen, setIncidentDialogOpen] = useState(false);

  // Drag state for reordering
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [localMonitors, setLocalMonitors] = useState<
    Array<{ id: string; monitorId: string; displayName: string; status: string; order: number }>
  >([]);

  // Fetch status page data
  const {
    data: statusPage,
    isLoading,
    error,
    refetch,
  } = trpc.statusPages.getById.useQuery(
    { id: id! },
    {
      enabled: !!id,
      retry: false,
      onSuccess: (data) => {
        setLocalMonitors(
          data.monitors.map((m) => ({
            id: m.id,
            monitorId: m.monitorId,
            displayName: m.displayName,
            status: m.status,
            order: m.order,
          }))
        );
      },
    }
  );

  // Fetch incidents for the status page
  const incidentsQuery = trpc.statusPages.publicIncidents.useQuery(
    { slug: statusPage?.slug || '', limit: 10 },
    { enabled: !!statusPage?.slug }
  );

  // Reorder mutation
  const reorderMutation = trpc.statusPages.reorderMonitors.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  // Remove monitor mutation
  const removeMonitorMutation = trpc.statusPages.removeMonitor.useMutation({
    onSuccess: () => {
      refetch();
      setSnackbarMessage('Monitor removed from status page');
      setSnackbarOpen(true);
    },
  });

  // Create incident mutation
  const createIncidentMutation = trpc.incidents.create.useMutation({
    onSuccess: () => {
      setIncidentDialogOpen(false);
      incidentsQuery.refetch();
      setSnackbarMessage('Incident created successfully');
      setSnackbarOpen(true);
    },
  });

  // Handle drag start
  const handleDragStart = useCallback((index: number) => {
    setDraggedIndex(index);
  }, []);

  // Handle drag over
  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (draggedIndex === null || draggedIndex === index) return;

      setLocalMonitors((prev) => {
        const newList = [...prev];
        const draggedItem = newList[draggedIndex];
        if (!draggedItem) return prev;
        newList.splice(draggedIndex, 1);
        newList.splice(index, 0, draggedItem);
        return newList.map((m, i) => ({ ...m, order: i }));
      });
      setDraggedIndex(index);
    },
    [draggedIndex]
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    if (id && localMonitors.length > 0) {
      reorderMutation.mutate({
        pageId: id,
        monitorIds: localMonitors.map((m) => m.monitorId),
      });
    }
  }, [id, localMonitors, reorderMutation]);

  // Handle remove monitor
  const handleRemoveMonitor = (monitorId: string) => {
    if (!id) return;
    removeMonitorMutation.mutate({ pageId: id, monitorId });
  };

  // Copy URL to clipboard
  const copyUrl = () => {
    if (statusPage) {
      const url = `${window.location.origin}/app/status/${statusPage.slug}`;
      navigator.clipboard.writeText(url);
      setSnackbarMessage('URL copied to clipboard');
      setSnackbarOpen(true);
    }
  };

  // Get public URL
  const getPublicUrl = () => {
    if (!statusPage) return '';
    return `${window.location.origin}/app/status/${statusPage.slug}`;
  };

  if (isLoading) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Typography color="text.primary">Status Page Details</Typography>
        </Breadcrumbs>
        <DetailSkeleton />
      </Box>
    );
  }

  if (error || !statusPage) {
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
            Status Pages
          </Link>
          <Typography color="text.primary">Status Page Details</Typography>
        </Breadcrumbs>

        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Status page not found
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            The status page with ID &quot;{id}&quot; could not be found.
          </Typography>
          <Button
            component={RouterLink}
            to="/status-pages"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
          >
            Back to Status Pages
          </Button>
        </Paper>
      </Box>
    );
  }

  // Convert incidents to IncidentData format
  const incidents: IncidentData[] = (incidentsQuery.data || []).map((incident) => ({
    id: incident.id,
    monitorId: incident.monitorId,
    monitorDisplayName: incident.monitorDisplayName,
    status: incident.status as 'ONGOING' | 'RESOLVED',
    cause: incident.cause,
    startedAt: incident.startedAt,
    resolvedAt: incident.resolvedAt,
    durationMs: incident.durationMs,
    isManual: incident.isManual,
    title: incident.title,
    message: incident.message,
  }));

  const activeIncidents = incidents.filter((i) => i.status === 'ONGOING');
  const recentIncidents = incidents.filter((i) => i.status === 'RESOLVED').slice(0, 5);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/status-pages" color="inherit" underline="hover">
          Status Pages
        </Link>
        <Typography color="text.primary">{statusPage.name}</Typography>
      </Breadcrumbs>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {statusPage.name}
            </Typography>
            <Chip
              icon={statusPage.isPublic ? <PublicIcon /> : <LockIcon />}
              label={statusPage.isPublic ? 'Public' : 'Private'}
              size="small"
              color={statusPage.isPublic ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
          {statusPage.description && (
            <Typography variant="body1" color="text.secondary">
              {statusPage.description}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {statusPage.isPublic && (
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              href={getPublicUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              Preview
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            component={RouterLink}
            to={`/status-pages/${id}/edit`}
          >
            Edit
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Info & Monitors */}
        <Grid item xs={12} lg={8}>
          {/* Page Info Card */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Page Information
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Public URL
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    /status/{statusPage.slug}
                  </Typography>
                  <Tooltip title="Copy URL">
                    <IconButton size="small" onClick={copyUrl}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body1">{formatDate(statusPage.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Primary Color
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      borderRadius: 0.5,
                      backgroundColor: statusPage.branding?.primaryColor || '#3B82F6',
                      border: 1,
                      borderColor: 'divider',
                    }}
                  />
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {statusPage.branding?.primaryColor || '#3B82F6'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Logo
                </Typography>
                {statusPage.branding?.logoUrl ? (
                  <Link
                    href={statusPage.branding.logoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    View logo
                  </Link>
                ) : (
                  <Typography variant="body1" color="text.disabled">
                    Not set
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* Monitors Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Monitors ({localMonitors.length})
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                component={RouterLink}
                to={`/status-pages/${id}/edit`}
              >
                Add Monitor
              </Button>
            </Box>

            {localMonitors.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {localMonitors.map((monitor, index) => (
                  <Box
                    key={monitor.monitorId}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      backgroundColor: draggedIndex === index ? 'action.selected' : 'transparent',
                      borderRadius: 1,
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <Tooltip title="Drag to reorder">
                      <IconButton size="small" sx={{ cursor: 'grab' }}>
                        <DragIndicatorIcon />
                      </IconButton>
                    </Tooltip>
                    <Box sx={{ flex: 1 }}>
                      <StatusPageMonitorRow
                        monitor={{
                          id: monitor.id,
                          displayName: monitor.displayName,
                          status: monitor.status as StatusPageMonitorStatus,
                          uptimePercentage: 99.9, // TODO: Get from actual data
                          avgResponseTime: null,
                          lastCheckedAt: null,
                        }}
                        days={30}
                        primaryColor={statusPage.branding?.primaryColor}
                        showDetails={false}
                      />
                    </Box>
                    <Tooltip title="Remove from status page">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMonitor(monitor.monitorId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            ) : (
              <Alert severity="info">
                No monitors added yet. Add monitors to display their status on this page.
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Right Column - Incidents */}
        <Grid item xs={12} lg={4}>
          {/* Create Incident Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<BuildIcon />}
            onClick={() => setIncidentDialogOpen(true)}
            disabled={localMonitors.length === 0}
            sx={{ mb: 3 }}
          >
            Create Manual Incident
          </Button>

          {/* Active Incidents */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <WarningIcon color="warning" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Active Incidents
              </Typography>
            </Box>

            {activeIncidents.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {activeIncidents.map((incident) => (
                  <IncidentCard
                    key={incident.id}
                    incident={incident}
                    primaryColor={statusPage.branding?.primaryColor}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No active incidents
              </Typography>
            )}
          </Paper>

          {/* Recent Incidents */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Recent Incidents
            </Typography>

            {recentIncidents.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentIncidents.map((incident) => (
                  <IncidentCard
                    key={incident.id}
                    incident={incident}
                    primaryColor={statusPage.branding?.primaryColor}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recent incidents
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create Incident Dialog */}
      <CreateIncidentDialog
        open={incidentDialogOpen}
        onClose={() => setIncidentDialogOpen(false)}
        monitors={localMonitors}
        onSubmit={(data) => createIncidentMutation.mutate(data)}
        isLoading={createIncidentMutation.isLoading}
      />
    </Box>
  );
}

export default StatusPageDetailPage;
